<!-- LayoutBuilder.vue (Vue 2, DRY version with center in the middle) -->
<template>
  <div class="invoice-layout">
    <div>
      <button @click="showDebug = true">show debug</button>
    </div>

    <div v-if="showDebug" class="debug-modal">
      <div class="debug-modal--wrap">
        <button @click="showDebug = false" class="close-btn">x</button>
        <div class="pre-wrap">
          <pre v-for="(items, key) in lists" :key="key">{{ key }}: {{ items }}</pre>
        </div>
      </div>
    </div>

    <!-- Render sections in the intended order: top -> center -> bottom -->
    <section v-for="section in layout" :key="section.id" class="invoice-section" :class="section.sectionClass">
      <!-- Top / Bottom areas are droppable -->
      <div v-for="area in section.areas" :key="area.key" class="invoice-area" :class="area.areaClass">
        <div>
          <InvoiceElementDropdown
            :available-elements="availableFor(area.key)"
            :list="area.key"
            @add-element="addElement"
            @remove-element="removeElement"
            @reset-config="resetConfig"
          />
        </div>

        <draggable
          v-model="lists[area.key]"
          :group="listConfigs[area.key].group"
          tag="ul"
          item-key="uuid"
          :animation="200"
          class="element-list-group"
          @start="drag = true"
          @end="drag = false"
        >
          <li
            v-for="element in lists[area.key]"
            :key="element.uuid"
            class="element-list-group-item"
            :class="element.list"
          >
            <i
              :class="element.fixed ? 'fa fa-anchor' : 'glyphicon glyphicon-pushpin'"
              @click="element.fixed = !element.fixed"
              aria-hidden="true"
            />
            {{ element.name }} {{ element.uuid }}

            <button class="remove-btn" @click.stop="removeItem(area.key, element.uuid)">✖</button>
          </li>

          <!-- <li v-if="(lists[area.key] || []).length === 0" class="list-placeholder">Drop here or select new element</li> -->
        </draggable>
      </div>
    </section>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import InvoiceElementDropdown from '@/components/layouts/InvoiceElementDropdown'

export default {
  name: 'LayoutBuilder',
  components: { draggable, InvoiceElementDropdown },
  data() {
    return {
      availableElements: [
        // your data
        { name: 'Your logo', type: 'element-type-company-logo', unique: false },
        { name: 'Your company name', type: 'element-type-company-name', unique: false },
        { name: 'Your name', type: 'element-type-company-username', unique: false },
        { name: 'Your address', type: 'element-type-company-address', unique: false },
        { name: 'Your KVK number', type: 'element-type-company-kvk', unique: false },
        { name: 'Your Website', type: 'element-type-company-website', unique: false },
        { name: 'Your Bank Account', type: 'element-type-company-bank', unique: false },
        // client
        { name: 'Client company name', type: 'element-type-client-name', unique: false },
        { name: 'Client name', type: 'element-type-client-username', unique: false },
        { name: 'Client address', type: 'element-type-client-address', unique: false },
        { name: 'Client kvk', type: 'element-type-client-kvk', unique: false },
        // amounts
        { name: 'Invoice lines', type: 'element-type-invoice-lines', unique: false },
        { name: 'Invoice message', type: 'element-type-invoice-message', unique: false },
      ],
      layout: [
        {
          id: 'top',
          sectionClass: 'invoice-top',
          areas: [
            {
              key: 'list-top-left',
              areaClass: 'invoice-top-left',
              group: { name: 'outer', pull: true, put: ['outer'] },
              allowedTypes: [
                'element-type-company-logo',
                'element-type-company-name',
                'element-type-company-address',
                'element-type-company-website',
              ],
              defaultItems: [{ type: 'element-type-company-logo' }]
            },
            {
              key: 'list-top-right',
              areaClass: 'invoice-top-right',
              group: { name: 'outer', pull: true, put: ['outer'] },
              allowedTypes: ['element-type-client-name', 'element-type-client-username', 'element-type-client-address'],
            },
          ],
        },
        {
          id: 'center',
          sectionClass: 'invoice-center',
          areas: [
            {
              key: 'list-center',
              areaClass: 'invoice-center',
              group: { name: 'center', pull: false, put: false },
              allowedTypes: ['element-type-invoice-lines', 'element-type-invoice-message'],
              defaultItems: [{ type: 'element-type-invoice-lines' }, { type: 'element-type-invoice-message' }]
            },
          ],
        },
        {
          id: 'bottom',
          sectionClass: 'invoice-bottom',
          areas: [
            {
              key: 'list-bottom-left',
              areaClass: 'invoice-bottom-left',
              group: { name: 'outer', pull: true, put: ['outer'] },
            },
            {
              key: 'list-bottom-right',
              areaClass: 'invoice-bottom-right',
              group: { name: 'outer', pull: true, put: ['outer'] },
            },
          ],
        },
      ],
      listConfigs: {},
      lists: {},
      drag: false,
      uidCounter: 1,
      showDebug: false,
    }
  },

  computed: {
    areaIndex() {
      const idx = {}
      this.layout.forEach((s) =>
        s.areas.forEach((a) => {
          idx[a.key] = a
        })
      )
      return idx
    },
  },

  created() {
    this.setupData()
  },

  methods: {
    setupData() {
      const lists = {}
      const listConfigs = {}

      const makeItem = (areaKey, base) => ({
        uuid: `${areaKey}_${this.uidCounter++}_${Date.now()}`,
        name: base.name, // filled below
        order: 0, // set below
        fixed: false,
        list: areaKey,
      })

      // quick resolver by type -> availableElements entry
      const byType = this.availableElements.reduce((m, el) => {
        m[el.type] = el
        return m
      }, {})

      this.layout.forEach((section) => {
        section.areas.forEach((area) => {
          listConfigs[area.key] = { group: area.group }

          // base empty list
          lists[area.key] = []

          // if area has defaults, create items
          if (Array.isArray(area.defaultItems)) {
            area.defaultItems.forEach((d, i) => {
              const src = byType[d.type]
              if (!src) return // silently skip unknown types
              const item = makeItem(area.key, { name: src.name })
              item.order = i + 1
              lists[area.key].push(item)
            })
          }
        })
      })

      this.lists = lists
      this.listConfigs = listConfigs
    },
    availableFor(areaKey) {
      const area = this.areaIndex[areaKey]
      if (!area || !Array.isArray(area.allowedTypes) || area.allowedTypes.length === 0) {
        return this.availableElements
      }
      return this.availableElements.filter((el) => area.allowedTypes.includes(el.type))
    },
    addElement({ element: el, list: listName }) {
      if (!listName) return
      const newItem = {
        uuid: `${listName}_${this.uidCounter++}_${Date.now()}`,
        name: el.name,
        order: (this.lists[listName].length || 0) + 1,
        fixed: false,
        list: listName,
      }
      this.$set(this.lists, listName, [...this.lists[listName], newItem])
    },

    removeItem(listName, uuid) {
      this.$set(
        this.lists,
        listName,
        this.lists[listName].filter((item) => item.uuid !== uuid)
      )
    },

    removeElement() {
      console.log('removeElement')
    },
    resetConfig() {
      console.log('resetConfig')
    },
  },
}
</script>


<style lang="scss" scoped>
.debug-modal {
  max-width: 900px;
  width: 100%;
  padding: 2rem;
  background: white;
  position: absolute;
  top: 50%;
  left: 50%;
  height: calc(100vh - 100px);
  transform: translate(-50%, -50%);
  z-index: 9001;
  overflow-y: auto;
  border: 4px solid black;
  border-radius: 24px;
  &--wrap {
    position: relative;
    .pre-wrap {
      background: #fff6b2;
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      pre {
        font-size: 10px;
        padding: 1rem;
      }
    }
  }
  .close-btn {
    position: absolute;
    right: -1rem;
    top: -1rem;
  }
}

.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}
.sortable-ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.element-list-group {
  height: 100%;
  min-height: 20px;
  gap: 0.5rem;

  display: flex;
  flex-direction: column;
  padding-left: 0;
  margin-bottom: 0;
  border-radius: 6px;

  .element-list-group-item {
    border-radius: $radiusMd;
    position: relative;
    display: block;
    padding: 0.75rem 1.25rem;
    background-color: #fff;
    border: 1px solid rgba(0, 0, 0, 0.125);

    &.list-top-left {
      background: #ffb2c2;
    }
    &.list-top-right {
      background: #fff6b2;
    }
    &.list-bottom-left {
      background: #b2ffdd;
    }
    &.list-bottom-right {
      background: #b2cfff;
    }
  }
  .list-placeholder {
    opacity: 0.6;
    font-style: italic;
    padding: 0.5rem;
  }
}

.invoice-layout {
  display: flex;
  flex-direction: column;
  row-gap: 20px;

  .invoice-section {
    padding: 1rem;
    border-radius: $radiusMd;
    border: 1px solid #ececec;
    background: white;

    .invoice-area {
      padding: 12px;
      border: 1px dashed #cfcfcf; // visual guide
      border-radius: $radiusMd;
      min-height: 64px;
      box-sizing: border-box;
    }
  }

  /* Top and bottom sections → row direction */
  .invoice-top,
  .invoice-bottom {
    display: flex;
    flex-direction: row;
    column-gap: 1rem;

    .invoice-area {
      flex: 1 1 0;
    }
  }

  /* Center section → stacked */
  .invoice-center {
    display: flex;
    flex-direction: column;
    row-gap: 1rem;
  }

  /* Responsive tweak: stack left/right on small screens */
  @media (max-width: 640px) {
    .invoice-top,
    .invoice-bottom {
      flex-direction: column;
      row-gap: 1rem;
    }
  }
}

.remove-btn {
  background: none;
  border: none;
  color: red;
  cursor: pointer;
  margin-left: 8px;
  font-size: 14px;
}
.remove-btn:hover {
  color: darkred;
}
</style>
